import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Home,
  BookOpen,
  BarChart3,
  MessageSquare,
  Award,
  LogOut,
  Moon,
  Sun,
  LayoutDashboard,
  GraduationCap,
  User as UserIcon,
  HelpCircle,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useTenantConfig } from "@/contexts/TenantConfigContext";
import { isBeautySnsLabConfig, isBeautySnsLabHost, BEAUTY_SNS_LAB_BRAND } from "@/config/beautySnsLab";
import { useSchoolFeatures } from "@/hooks/useSchoolFeatures";

const menuItems = [
  { title: "ホーム", url: "/student/dashboard", icon: Home },
  { title: "動画学習", url: "/student/courses", icon: BookOpen },
  { title: "進捗確認", url: "/student/progress", icon: BarChart3 },
  { title: "AIに相談", url: "/student/chat", icon: MessageSquare },
  { title: "達成", url: "/student/achievements", icon: Award },
  { title: "ヘルプ", url: "/student/help", icon: HelpCircle },
];

export function StudentSidebar() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const { config } = useTenantConfig();
  const features = useSchoolFeatures();

  const isAdminView = location.pathname.startsWith('/dashboard') ||
                      location.pathname.startsWith('/students') ||
                      location.pathname.startsWith('/progress') ||
                      location.pathname.startsWith('/analytics') ||
                      location.pathname.startsWith('/chat') ||
                      location.pathname.startsWith('/videos') ||
                      location.pathname.startsWith('/escalations');
  const canSwitchView = user?.role === 'admin' || user?.role === 'educator';
  const switchButtonLabel = isAdminView ? "学習者ページへ移動" : "管理者ページへ戻る";
  const isBeautyTenant =
    isBeautySnsLabConfig(config) ||
    (typeof window !== "undefined" && isBeautySnsLabHost(window.location.hostname));
  const visibleMenuItems = menuItems.filter((item) => {
    return !("feature" in item) || features[item.feature] === true;
  });
  const sidebarLabel =
    config?.brand?.name || (isBeautyTenant ? BEAUTY_SNS_LAB_BRAND.name : "学習メニュー");
  const logoUrl =
    config?.brand?.logo_url || (isBeautyTenant ? BEAUTY_SNS_LAB_BRAND.logo_url : null);

  const handleViewSwitch = () => {
    if (isAdminView) {
      // Beauty SNS Lab: コース一覧へ、他: ダッシュボードへ
      navigate(isBeautyTenant ? '/student/courses' : '/student/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = async () => {
    // Clear chat session flag before logout
    sessionStorage.removeItem('chat_page_visited');

    const { error } = await supabase.auth.signOut();

    if (error) {
      toast({
        title: "エラー",
        description: "ログアウトに失敗しました",
        variant: "destructive",
      });
    } else {
      toast({
        title: "ログアウト成功",
        description: "またお会いしましょう",
      });
      navigate("/auth");
    }
  };

  return (
    <Sidebar
      className={isCollapsed ? "w-16" : "w-64"}
      collapsible="icon"
      role="navigation"
      aria-label="学習メニュー"
    >
      <SidebarContent>
        <SidebarGroup>
          <div className="flex items-center justify-between px-2 pt-2 pb-4 mb-2">
            <SidebarGroupLabel className={isCollapsed ? "sr-only" : "flex h-16 items-center gap-2 px-2 py-2"}>
              {logoUrl ? (
                <img src={logoUrl} alt={sidebarLabel} className="h-12 max-w-[190px] object-contain" />
              ) : (
                sidebarLabel
              )}
            </SidebarGroupLabel>
            <SidebarTrigger className="ml-auto" />
          </div>
          <SidebarGroupContent>
            <SidebarMenu aria-label="メニュー" className="space-y-1">
              {visibleMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-muted text-primary font-medium"
                          : "hover:bg-muted/50"
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="h-6 w-6" aria-hidden="true" />
                          {!isCollapsed && <span className="text-lg font-medium">{item.title}</span>}
                          {isActive && <span className="sr-only">（現在のページ）</span>}
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="p-2 space-y-2">
          {!isCollapsed && (
            <>
              {/* User Info */}
              <div className="flex items-center gap-2 px-2 py-1">
                <UserIcon className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground truncate flex-1">
                  {user?.email}
                </span>
              </div>

              {/* View Switch Button */}
              {canSwitchView && (
                <Button
                  variant={isAdminView ? "default" : "outline"}
                  size="sm"
                  onClick={handleViewSwitch}
                  className="w-full gap-2 justify-start"
                  aria-label={switchButtonLabel}
                >
                  {isAdminView ? (
                    <>
                      <GraduationCap className="h-5 w-5" aria-hidden="true" />
                      <span>{switchButtonLabel}</span>
                    </>
                  ) : (
                    <>
                      <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
                      <span>{switchButtonLabel}</span>
                    </>
                  )}
                </Button>
              )}

              {/* Theme Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 justify-start"
                    aria-label={theme === "dark" ? "ダークモード（クリックで変更）" : "ライトモード（クリックで変更）"}
                  >
                    {theme === "dark" ? (
                      <>
                        <Moon className="h-5 w-5" aria-hidden="true" />
                        <span>ダークモード</span>
                      </>
                    ) : (
                      <>
                        <Sun className="h-5 w-5" aria-hidden="true" />
                        <span>ライトモード</span>
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top">
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    ライトモード
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    ダークモード
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Logout Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full gap-2 justify-start text-destructive hover:text-destructive"
                aria-label="ログアウト"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                <span>ログアウト</span>
              </Button>

              {/* Legal Links */}
              <Separator />
              <div className="flex justify-center gap-4 text-xs text-muted-foreground">
                <Link to="/student/terms" className="hover:text-primary underline">
                  利用規約
                </Link>
                <Link to="/student/privacy" className="hover:text-primary underline">
                  プライバシー
                </Link>
              </div>
            </>
          )}

          {isCollapsed && (
            <div className="flex flex-col gap-1">
              {/* View Switch (icon only) */}
              {canSwitchView && (
                <Button
                  variant={isAdminView ? "default" : "outline"}
                  size="icon"
                  onClick={handleViewSwitch}
                  title={switchButtonLabel}
                  aria-label={switchButtonLabel}
                >
                  {isAdminView ? (
                    <GraduationCap className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
                  )}
                </Button>
              )}

              {/* Theme Switcher (icon only) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={theme === "dark" ? "ダークモード（クリックで変更）" : "ライトモード（クリックで変更）"}
                  >
                    {theme === "dark" ? (
                      <Moon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Sun className="h-5 w-5" aria-hidden="true" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="right">
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    ライトモード
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    ダークモード
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Logout (icon only) */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                title="ログアウト"
                aria-label="ログアウト"
                className="text-destructive hover:text-destructive"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
